import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, CircularProgress, Typography, Alert, Button } from '@mui/material';
import { clearAuth0State } from '../utils/clearAuth0State';

const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isLoading, isAuthenticated, error, user, getAccessTokenSilently, handleRedirectCallback, loginWithRedirect } = useAuth0();
  const [debugInfo, setDebugInfo] = useState<any>({});
  const [timeoutReached, setTimeoutReached] = useState(false);
  const [processing, setProcessing] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [isInvalidStateError, setIsInvalidStateError] = useState(false);

  useEffect(() => {
    const currentState = {
      isLoading,
      isAuthenticated,
      error: error?.message,
      user: user?.email,
      location: location.pathname + location.search,
      timestamp: new Date().toISOString()
    };
    setDebugInfo(currentState);
    console.log('🔄 DEBUG: AuthCallback state update', currentState);
  }, [isLoading, isAuthenticated, error, user, location]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (processing && !isAuthenticated && !error) {
        console.log('⏰ DEBUG: AuthCallback timeout reached (15 seconds)');
        setTimeoutReached(true);
        setProcessing(false);
      }
    }, 15000); // 15 second timeout

    // CONSERVATIVE APPROACH: Don't treat "Invalid state" as a failure during processing
    // These are normal Auth0 internal errors that usually resolve themselves
    // Only trigger recovery if authentication completely fails after processing
    
    // Explicitly handle the redirect callback if we have query params
    if (location.search && processing && !isInvalidStateError) {
      console.log('🔄 DEBUG: Processing Auth0 redirect callback...');
      handleRedirectCallback()
        .then(() => {
          console.log('✅ DEBUG: Redirect callback processed successfully');
          setProcessing(false);
        })
        .catch((callbackError) => {
          console.error('🚨 DEBUG: Redirect callback error:', callbackError);
          
          // Don't immediately trigger recovery for "Invalid state" errors
          // Let the auth flow complete first and see if user gets authenticated
          if (callbackError.message && callbackError.message.toLowerCase().includes('invalid state')) {
            console.log('⚠️ DEBUG: Invalid state error during callback - monitoring auth result...');
            // Set a flag to monitor this, but don't trigger recovery yet
            setProcessing(false);
          } else {
            // For other errors, trigger recovery
            setProcessing(false);
          }
        });
    }

    return () => clearTimeout(timeout);
  }, [location.search, processing, handleRedirectCallback, isInvalidStateError]);

  // ONLY trigger recovery if authentication genuinely fails after a delay
  // This gives Auth0 time to complete its internal processing
  useEffect(() => {
    if (error && error.message && error.message.toLowerCase().includes('invalid state')) {
      // Wait to see if authentication succeeds despite the error
      const recoveryTimer = setTimeout(() => {
        if (!isAuthenticated && error) {
          console.log('🚨 DEBUG: Authentication failed after Invalid State error - triggering recovery...');
          setIsInvalidStateError(true);
          setProcessing(false);
        } else {
          console.log('✅ DEBUG: Auth succeeded despite Invalid State error - no recovery needed');
        }
      }, 2000); // Wait 2 seconds to see if auth completes

      return () => clearTimeout(recoveryTimer);
    }
  }, [error, isAuthenticated]);

  // Auto-recovery for invalid state error
  const handleInvalidStateRecovery = async () => {
    console.log('🔧 Starting Auth0 Invalid State recovery...');
    
    // Clear all Auth0 state
    clearAuth0State();
    
    // Wait a moment for state to clear
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Increment retry count
    const newRetryCount = retryCount + 1;
    setRetryCount(newRetryCount);
    
    if (newRetryCount <= 3) {
      console.log(`🔄 Attempting fresh login (attempt ${newRetryCount}/3)...`);
      // Use window.location.href for a complete page reload and fresh start
      window.location.href = '/';
    } else {
      console.log('❌ Maximum retry attempts reached');
      alert('Authentication failed after multiple attempts. Please try again later or contact support.');
    }
  };

  // Auto-trigger recovery for invalid state (but only once per component mount)
  useEffect(() => {
    if (isInvalidStateError && retryCount === 0) {
      console.log('🚀 AUTO-RECOVERY: Triggering automatic invalid state recovery...');
      const recoveryTimer = setTimeout(() => {
        handleInvalidStateRecovery();
      }, 3000); // Increased from 1 second to 3 seconds for better UX
      
      // Store timer ID so we can cancel it
      return () => clearTimeout(recoveryTimer);
    }
  }, [isInvalidStateError, retryCount]);

  // Redirect to dashboard on successful authentication (even if there were internal errors)
  useEffect(() => {
    if (isAuthenticated && user && !processing && !isInvalidStateError) {
      console.log('✅ DEBUG: Authentication successful, redirecting to dashboard...');
      console.log('✅ DEBUG: Authenticated user:', { 
        email: user.email, 
        name: user.name,
        sub: user.sub 
      });
      navigate('/dashboard');
    }
  }, [isAuthenticated, user, processing, navigate, isInvalidStateError]);

  // Handle manual retry with full page reload
  const handleRetry = async () => {
    console.log('🔄 Manual retry requested...');
    clearAuth0State();
    await new Promise(resolve => setTimeout(resolve, 500));
    // Force complete page reload to start fresh
    window.location.href = '/';
  };

  // Handle clear and redirect to home with page reload
  const handleClearAndHome = () => {
    console.log('🏠 Clearing state and redirecting to home...');
    clearAuth0State();
    // Force complete page reload
    window.location.href = '/';
  };

  // Handle super aggressive recovery - clear everything and reload
  const handleForceReload = () => {
    console.log('💥 FORCE RELOAD: Clearing everything and starting fresh...');
    
    // Clear all storage
    clearAuth0State();
    localStorage.clear();
    sessionStorage.clear();
    
    // Clear URL params and force reload
    window.location.href = window.location.origin + '/';
  };

  if (processing && !error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, textAlign: 'center' }}>
          Processing Authentication...
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary', textAlign: 'center' }}>
          Please wait while we complete your login
        </Typography>
      </Box>
    );
  }

  if (isInvalidStateError) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" px={3}>
        <Alert severity="warning" sx={{ mb: 3, maxWidth: 500 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            🔐 Authentication Error: Invalid state
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Auth0 state validation failed. This usually happens due to browser cache issues or multiple login attempts.
          </Typography>
          {retryCount === 0 && (
            <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 'medium' }}>
              🔄 Automatic recovery starting in 3 seconds...
            </Typography>
          )}
          {retryCount > 0 && retryCount <= 3 && (
            <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 'medium' }}>
              ⚡ Recovery attempt {retryCount}/3 in progress...
            </Typography>
          )}
        </Alert>
        
        {retryCount > 0 && retryCount <= 3 && (
          <Box textAlign="center" sx={{ mb: 3 }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Clearing cache and restarting...
            </Typography>
          </Box>
        )}
        
        <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }} sx={{ mb: 2 }}>
          <Button variant="contained" onClick={handleRetry} color="primary">
            🔄 Retry Now
          </Button>
          <Button variant="outlined" onClick={handleClearAndHome} color="secondary">
            🏠 Return Home
          </Button>
        </Box>
        
        {retryCount > 1 && (
          <Box textAlign="center" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
              Still having issues? Try a complete reset:
            </Typography>
            <Button 
              variant="contained" 
              onClick={handleForceReload} 
              color="error"
              size="small"
            >
              💥 Force Complete Reset
            </Button>
          </Box>
        )}
        
        <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1, maxWidth: 400, textAlign: 'center' }}>
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            💡 <strong>What's happening:</strong> This error occurs when Auth0's security state gets corrupted. 
            The system automatically clears cached data and restarts authentication.
            <br /><br />
            <strong>Manual Control:</strong> Click "🔄 Retry Now" to start recovery immediately, or wait for auto-recovery.
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" px={3}>
        <Alert severity="error" sx={{ mb: 3, maxWidth: 500 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Authentication Error
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {error.message || 'An unexpected authentication error occurred.'}
          </Typography>
        </Alert>
        
        <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Button variant="contained" onClick={handleRetry} color="primary">
            🔄 Retry Login
          </Button>
          <Button variant="outlined" onClick={handleClearAndHome}>
            🏠 Return Home
          </Button>
        </Box>
        
        <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1, maxWidth: 600 }}>
          <Typography variant="caption" component="pre" sx={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>
            {JSON.stringify(debugInfo, null, 2)}
          </Typography>
        </Box>
      </Box>
    );
  }

  if (timeoutReached) {
    return (
      <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh" px={3}>
        <Alert severity="warning" sx={{ mb: 3, maxWidth: 500 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Authentication Timeout
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            The authentication process took longer than expected.
          </Typography>
        </Alert>
        
        <Box display="flex" gap={2} flexDirection={{ xs: 'column', sm: 'row' }}>
          <Button variant="contained" onClick={handleRetry} color="primary">
            🔄 Retry Login
          </Button>
          <Button variant="outlined" onClick={handleClearAndHome}>
            🏠 Return Home
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="100vh">
      <CircularProgress size={60} />
      <Typography variant="h6" sx={{ mt: 2 }}>
        Finalizing Authentication...
      </Typography>
    </Box>
  );
};

export default AuthCallback; 